import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ActKc2Data } from 'gql/graphql';
import { WorkOrderStatusEnum, ActKc2StatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { getActKC2StatusItem } from 'pages/Project/Common/types';
import { Loader } from 'uikit';
import { newDate, renameKeys, toISOString } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { getDateIncludingCurrentTime } from 'utils/formatDateToLocale';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useGetActKc2 } from '../hooks/useGetActKc2';
import { gqlUpdateActKC2 } from '../workOrderQueries';

import { ActKC2EditForm, ActKC2Fields } from './ActKC2EditForm';
import { getActKC2SeasonItem } from './ActKC2SeasonField';
import { ActKC2Tools } from './ActKC2Tools';

import './AddActKC2.css';

export interface IActKC2Data extends Omit<ActKc2Data, 'date' | 'createAt' | 'status' | 'season'> {
    date?: Date | null;
    createAt: Date;
    status: { id: ActKc2StatusEnum; label: string };
    constructionType?: string | null;
    objectName?: string | null;
    season: { id: any; label: string };
    reportingDate: Date[];
}

export const parseActKC2 = R.pipe(
    R.evolve({
        date: newDate,
        createAt: newDate,
        status: getActKC2StatusItem,
        season: getActKC2SeasonItem,
        inflationSeason: getActKC2SeasonItem,
    }),
    (data: any) => ({
        ...data,
        reportingDate: [newDate(data.reportingStartDate), newDate(data.reportingFinishDate)],
        correctionDate: data.correctionDate ? new Date(data.correctionDate) : data.correctionDate,
    }),
    R.dissoc('isDistributionOfMtrInflation'),
    renameKeys({ season: 'seasonId' }),
    renameKeys({ inflationSeason: 'inflationSeasonId' }),
);

// TODO: any
const serializeActKC2: any = R.pipe(
    R.omit([
        'authoredBy',
        'contractor',
        'contractorAddress',
        'costGroup',
        'createdAt',
        'object',
        'reportingDate',
        'updatedAt',
    ]),
    R.evolve({
        status: R.prop('id'),
        seasonId: R.prop('id'),
        inflationSeasonId: R.prop('id'),
        reportingStartDate: toISOString,
        reportingFinishDate: toISOString,
        date: getDateIncludingCurrentTime,
    }),
    R.objOf('actKC2Input'),
);

export interface IEditActKC2ModalProps {
    aktKC2Rules: IUseAccess['aktKC2'];
    isSKzSAct?: boolean | null;
    statusWorkOrder?: WorkOrderStatusEnum;
    actKC2Id?: number;
    onClose?: () => void;
    onRefresh?: () => void;
}

export const EditActKC2Modal = ({
    actKC2Id,
    onClose,
    onRefresh,
    aktKC2Rules,
    statusWorkOrder,
    isSKzSAct,
}: IEditActKC2ModalProps) => {
    const dispatch = useDispatch();

    const { id: projectId, orderId: workOrderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isDirtyFields, setIsDirtyFields] = useState<boolean>(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;

    const { getActKC2Response, getActKC2Reset } = useGetActKc2(actKC2Id);

    const dataResponse = useMemo(() => getActKC2Response?.uerActKC2?.data, [getActKC2Response?.uerActKC2?.data]);
    const defaultValues: IActKC2Data | undefined | null = useMemo(
        () =>
            dataResponse &&
            ({
                ...parseActKC2(dataResponse),
                constructionType: dataResponse?.object?.constructionType?.fullName,
                objectName: dataResponse?.object?.name,
            } as unknown as IActKC2Data),
        [dataResponse],
    );

    const constructionTypeCode = useMemo(
        () => dataResponse?.object?.constructionType?.code,
        [dataResponse?.object?.constructionType?.code],
    );

    const [isSubmitted, setIsSubmitted] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const handleOnEditCancel = useCallback(() => {
        if (isDirtyFields) {
            setIsOpenWarningDialog(true);
        } else {
            onClose?.();
        }
    }, [isDirtyFields, onClose]);

    const handleOnEditConfirm = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const [updateActKC2Data, updateActKC2Status, updateActKC2Error, updateActKC2] = useRequest({
        requestId: 'updateActKC2',
        query: gqlUpdateActKC2,
        withEffect: false,
    });

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(workOrderId),
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
        ],
    );

    const handleFormSubmit = useCallback(
        (formValues: any) => {
            dispatch(
                updateActKC2(
                    serializeActKC2({
                        ...R.omit(['isCopied'], formValues),
                        number: formValues.number ? Number(formValues.number) : null,
                        [ActKC2Fields.reportingStartDate]: getDateIncludingCurrentTime(formValues?.reportingDate?.[0]),
                        [ActKC2Fields.reportingFinishDate]: getDateIncludingCurrentTime(formValues?.reportingDate?.[1]),
                    }),
                ),
            );
            setIsSubmitted(true);

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC save button_pressed',
                ...analyticsData,
            });
        },
        [dispatch, updateActKC2, sendGpnAnalyticsEvent, analyticsData],
    );

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(updateActKC2Data?.uerUpdateActKC2?.validation);
    }, [updateActKC2Data?.uerUpdateActKC2?.validation]);

    useEffect(() => {
        if (
            isSubmitted &&
            updateActKC2Status === RequestStatus.SUCCESS &&
            updateActKC2Data?.uerUpdateActKC2.result === true
        ) {
            if (defaultValues?.status?.id !== updateActKC2Data?.uerUpdateActKC2?.data?.status) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_act KC choose status save button_pressed',
                    ...analyticsData,
                    costGroup: updateActKC2Data?.uerUpdateActKC2?.data?.costGroup,
                    actKC2Status: updateActKC2Data?.uerUpdateActKC2?.data?.status,
                    actKC2Id: updateActKC2Data?.uerUpdateActKC2?.data?.id,
                });
            }

            getActKC2Reset(undefined);
            onRefresh?.();
            onClose?.();
        }
    }, [
        updateActKC2Status,
        updateActKC2Data,
        isSubmitted,
        onClose,
        dispatch,
        onRefresh,
        defaultValues?.status?.id,
        sendGpnAnalyticsEvent,
        analyticsData,
        getActKC2Reset,
    ]);

    const isLoading = actKC2Id ? getActKC2Response?.uerActKC2?.data?.id !== actKC2Id : false;

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const handleSubmitWarningDialog = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const onConfirm = useMemo(
        () =>
            defaultValues?.status?.id === ActKc2StatusEnum.Accepted && defaultValues?.isInactive
                ? undefined
                : handleOnEditConfirm,
        [defaultValues?.isInactive, defaultValues?.status?.id, handleOnEditConfirm],
    );

    const btnCancelLabel = useMemo(
        () =>
            defaultValues?.status?.id === ActKc2StatusEnum.Accepted && defaultValues?.isInactive
                ? 'Закрыть'
                : 'Отменить',
        [defaultValues?.isInactive, defaultValues?.status?.id],
    );

    return (
        <ModalDialog
            title="Реквизиты Акта КС-2"
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={handleOnEditCancel}
            onConfirm={onConfirm}
            btnCancelLabel={btnCancelLabel}
            btnConfirmDisabled={isStatusWorkOrderCompleted || isStatusWorkOrderDelayed}
            leftPanel={
                !isLoading && (
                    <ActKC2Tools
                        status={defaultValues?.status?.id}
                        actKc2Id={actKC2Id}
                        number={defaultValues?.number}
                        date={defaultValues?.date}
                        costGroup={defaultValues?.costGroup}
                        onRefresh={onRefresh}
                        statusWorkOrder={statusWorkOrder}
                        isInactive={!!defaultValues?.isInactive}
                    />
                )
            }
        >
            <div id="addActKC2">
                {isLoading ? (
                    <Loader />
                ) : (
                    <ActKC2EditForm
                        defaultValues={defaultValues}
                        onSubmit={handleFormSubmit}
                        ref={formRef}
                        constructionTypeCode={constructionTypeCode}
                        actKC2Id={actKC2Id}
                        setIsDirty={setIsDirtyFields}
                        workOrderObjectId={dataResponse?.object?.id}
                        aktKC2Rules={aktKC2Rules}
                        isCopied={getActKC2Response?.uerActKC2?.data?.isCopied}
                        isInactive={getActKC2Response?.uerActKC2?.data?.isInactive}
                        isSKzSAct={isSKzSAct}
                        statusWorkOrder={statusWorkOrder}
                    />
                )}
            </div>
            <ErrorHandlingModal
                titleText="Редактирование акта КС-2"
                isSubmitted={isSubmitted}
                statusRequest={updateActKC2Status}
                dataRequest={updateActKC2Data?.uerUpdateActKC2}
                errorsRequest={updateActKC2Error}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

EditActKC2Modal.displayName = 'EditActKC2Modal';
